import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useQuery } from "@tanstack/react-query";
import { Activity, Anchor, LayoutDashboard, LogOut, Map, Radio, Ship, Settings, ShieldAlert, FlaskConical, Waves, SignalHigh, Factory, Maximize2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { get } from "@/lib/api";
import { FeedWatcher } from "@/components/FeedWatcher";
import { fmtLagos, fmtUtc, DEMO_CLOCK } from "@/lib/format";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "nav-dashboard-link" },
  { to: "/map", label: "Live AIS Map", icon: Map, id: "nav-map-link", end: true },
  { to: "/map/replay", label: "Scenario Replay", icon: Waves, id: "nav-replay-link" },
  { to: "/live/coverage", label: "AIS Coverage", icon: SignalHigh, id: "nav-coverage-link", end: true },
  { to: "/port-emissions", label: "Port Emissions", icon: Factory, id: "nav-port-emissions-link" },
  { to: "/sensors", label: "Sensors", icon: Radio, id: "nav-sensors-link" },
  { to: "/vessels", label: "Vessels", icon: Ship, id: "nav-vessels-link" },
  { to: "/events", label: "Events", icon: Activity, id: "nav-events-link" },
  { to: "/investigations", label: "Investigations", icon: FlaskConical, id: "nav-attributions-link" },
];

export function DemoBanner({ live }) {
  return (
    <div data-testid="synthetic-data-banner" className="flex items-center justify-center gap-2 border-b border-amber-500/30 bg-amber-500/10 px-4 py-1 font-mono text-xs font-semibold tracking-wider text-amber-400">
      <ShieldAlert className="h-3.5 w-3.5" />
      DEMO — SYNTHETIC sensors, events &amp; scenario vessels · Fictional port geometry · No regulatory findings
      {live && <span data-testid="live-ais-banner" className="ml-2 rounded border border-emerald-500/40 bg-emerald-500/10 px-1.5 text-emerald-300">LIVE AIS · {live}</span>}
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { data: live } = useQuery({ queryKey: ["live-status"], queryFn: () => get("/live/status"), refetchInterval: 30000 });
  return (
    <div className="flex h-screen flex-col bg-[#0A1612] text-slate-200">
      <DemoBanner live={live?.configured ? live.provider_label : null} />
      <div className="flex min-h-0 flex-1">
        <aside className="flex w-[232px] shrink-0 flex-col border-r border-[#1E3E30] bg-[#11241C]" data-testid="sidebar">
          <Link to="/" data-testid="sidebar-home-link" className="block px-5 pb-4 pt-5">
            <img src="/brand/nimacems.svg" alt="NIMACEMS" className="h-14 w-full rounded-xl bg-white object-contain px-3 py-2" data-testid="brand-nimacems-logo" />
            <div className="mt-2 font-display text-[11px] font-semibold uppercase tracking-[0.18em] text-lime-300">Nigeria Maritime · AOCEMS</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Tin Can Island · Pilot</div>
          </Link>
          <nav className="mt-2 flex flex-1 flex-col gap-0.5 px-3">
            {NAV.map(({ to, label, icon: Icon, id, end }) => (
              <NavLink key={to} to={to} end={end} data-testid={id} className={({ isActive }) => cn("flex items-center gap-3 rounded px-3 py-2 text-sm transition-colors", isActive ? "bg-lime-500/15 text-lime-200" : "text-slate-400 hover:bg-white/5 hover:text-slate-200")}>
                <Icon className="h-4 w-4" />{label}
              </NavLink>
            ))}
            {user?.role === "ADMIN" && (
              <NavLink to="/admin" data-testid="nav-admin-link" className={({ isActive }) => cn("mt-2 flex items-center gap-3 rounded px-3 py-2 text-sm transition-colors", isActive ? "bg-lime-500/15 text-lime-200" : "text-slate-400 hover:bg-white/5 hover:text-slate-200")}>
                <Settings className="h-4 w-4" />Admin
              </NavLink>
            )}
          </nav>
          <div className="mx-4 mb-3 rounded-xl border border-[#1E3E30] p-3" data-testid="sidebar-ups-credit">
            <img src="/brand/aocems.svg" alt="AOCEMS" className="h-7 w-auto rounded bg-white/95 px-1.5 py-0.5" />
            <div className="mt-2 text-[10px] uppercase tracking-wider text-slate-500">Managed &amp; built by</div>
            <div className="font-display text-[12px] font-bold leading-tight text-slate-200">United Platform Solutions Global<sup>™</sup></div>
          </div>
          <div className="border-t border-[#1E3E30] px-4 py-4">
            <div className="flex items-center gap-3">
              {user?.picture ? <img src={user.picture} alt="" className="h-8 w-8 rounded-full" /> : <div className="h-8 w-8 rounded-full bg-slate-700" />}
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm text-slate-200" data-testid="current-user-name">{user?.auth_provider === "open_access" ? "Demo Operator" : user?.name}</div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-lime-400" data-testid="current-user-role">{user?.role}{user?.auth_provider === "open_access" ? " · open access" : ""}</div>
              </div>
              {user?.auth_provider !== "open_access" && (
                <button data-testid="logout-button" onClick={async () => { await logout(); navigate("/dashboard"); }} className="rounded p-1.5 text-slate-500 transition-colors hover:bg-white/5 hover:text-slate-200" title="Sign out">
                  <LogOut className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </aside>
        <div className="flex min-w-0 flex-1 flex-col">
          <header className="flex h-11 shrink-0 items-center justify-between border-b border-[#1E3E30] bg-[#11241C]/80 px-6 backdrop-blur" data-testid="topbar">
            <div className="text-xs text-slate-500"><span className="font-semibold text-slate-300">NIMACEMS</span> · AOCEMS Nigeria · <span className="text-slate-300">Tin Can Island Port (pilot)</span></div>
            <div className="flex items-center gap-4 font-mono text-xs">
              <span className="hidden rounded-full border border-lime-500/30 px-2 py-0.5 text-[10px] uppercase tracking-wider text-lime-300 xl:inline">IMO GreenVoyage 2050</span>
              <span className="hidden rounded-full border border-lime-500/30 px-2 py-0.5 text-[10px] uppercase tracking-wider text-lime-300 xl:inline">MARPOL Annex VI</span>
              <button data-testid="page-fullscreen-btn" title="Full screen" onClick={() => (document.fullscreenElement ? document.exitFullscreen() : document.getElementById("app-main")?.requestFullscreen())} className="inline-flex items-center gap-1 rounded-full border border-white/10 px-2.5 py-0.5 text-slate-300 transition-colors hover:bg-white/10"><Maximize2 className="h-3.5 w-3.5" />Full screen</button>
              <ThemeToggle />
              <span className="text-slate-500">Demo clock</span>
              <span className="text-slate-200" data-testid="demo-clock-utc">{fmtUtc(DEMO_CLOCK, true)}</span>
              <span className="text-slate-500" data-testid="demo-clock-lagos">{fmtLagos(DEMO_CLOCK)}</span>
            </div>
          </header>
          <main id="app-main" className="min-h-0 flex-1 overflow-y-auto bg-[#0A1612] p-6">
            <FeedWatcher />
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
